import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import toastHandler from 'Utils/toastHandler';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import { ObrasApiV2 } from 'Data/Obras.api.v2';
import { IObraPermissionsRequest } from 'Data/interfaces/Obra/IObraPermissions';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';

export const useQueryUpdatePermissionsObra = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IObraPermissionsRequest) => {
      return ObrasApiV2.updatePermissions(variables);
    },
    onMutate: async (variables) => {
      const previousObras = queryClient.getQueryData<IObraResponse[]>(
        [OBRA_QUERY_KEY],
      );
      const previousUniqueObra = queryClient.getQueryData<IObraResponse>(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
      );

      const applyPermissions = (obra: IObraResponse): IObraResponse => {
        if (
          variables.permissions.constructionSiteInvitedUsersPermission.length === 0 &&
          variables.permissions.constructionSiteUsersPermission.length === 0
        ) {
          return obra;
        }

        const invitedUsers = obra.InvitedUsers.map((invitedUser: ISharedUser) => {
          const user = variables.permissions.constructionSiteInvitedUsersPermission.find(
            (item) => item.email === invitedUser.UserFk,
          );
          if (!user) { return invitedUser; }
          return {
            ...invitedUser,
            HasPlottingPermission: user.hasPlottingPermission,
            Privileges: user.privileges,
          };
        });

        const sharedUsers = obra.SharedUsers.map((sharedUser: ISharedUser) => {
          const user = variables.permissions.constructionSiteUsersPermission.find(
            (item) => item.userId === sharedUser.UserFk,
          );
          if (!user) { return sharedUser; }
          return {
            ...sharedUser,
            HasPlottingPermission: user.hasPlottingPermission,
            Privileges: user.privileges,
          };
        });

        return { ...obra, InvitedUsers: invitedUsers, SharedUsers: sharedUsers };
      };

      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (
        oldData,
      ) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === variables.constructionStieId) {
            return applyPermissions(obra);
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>([OBRA_QUERY_KEY, Number(variables.constructionStieId)], (
        oldData,
      ) => {
        if (oldData && oldData.ConstructionSiteId === variables.constructionStieId) {
          return applyPermissions(oldData);
        }
        return oldData;
      });

      return { previousObras, previousUniqueObra };
    },
    onSuccess: () => {
      toastHandler.showSuccess('Permissões da Obra atualizadas com sucesso!');
    },
    onError: (error: any) => {
      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao atualizar permissões!';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updatePermissionsObra: mutate,
    isLoading: isPending,
  };
};
